/**
 * Canvas Panel
 *
 * Shared component for rendering canvas surfaces. Used by both
 * Builder and Sandbox pages. Reads state from CanvasContext.
 */

import { useMemo, useEffect, useRef } from 'react';
import { useCanvases } from '../../contexts/CanvasContext';
import { CanvasTabs } from './CanvasTabs';
import { CanvasEmptyState } from './CanvasEmptyState';
import { SurfaceLayout } from './SurfaceLayout';
import { compareCanvases, compareSurfaces } from '../../types/canvases';
import type { AuiAction } from '../../types/canvases';

interface CanvasPanelProps {
  onAction?: (action: AuiAction, surfaceName: string, canvasId: string) => Promise<void>;
  emptyState?: React.ReactNode;
  headerActions?: React.ReactNode;
}

export function CanvasPanel({ onAction, emptyState, headerActions }: CanvasPanelProps) {
  const { canvases, surfaces, activeCanvasId, setActiveCanvasId, removeCanvas, reorderCanvases, renameCanvas, reorderSurfaces, hasCanvases, scrollToSurface, pendingUpdates, isActiveCanvasLoading, dispatch } =
    useCanvases();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const innerRafRef = useRef<number>(0);

  const canvasList = useMemo(
    () => [...canvases.values()].sort(compareCanvases),
    [canvases],
  );

  const activeCanvas = activeCanvasId ? canvases.get(activeCanvasId) : null;

  const activeSurfaces = useMemo(() => {
    if (!activeCanvasId) return [];
    return [...surfaces.values()]
      .filter((s) => s.canvasId === activeCanvasId)
      .sort(compareSurfaces);
  }, [surfaces, activeCanvasId]);

  // Scroll to surface when requested. Triple-RAF + a 50ms fallback poll
  // covers the worst case where the surface tab just switched: the active
  // canvas's surfaces re-render across a couple of frames, so the Card with
  // `data-surface-id` may not be queryable on the first inner RAF. We retry
  // up to ~250ms total before giving up and clearing the scroll target.
  useEffect(() => {
    if (!scrollToSurface) return;

    if (scrollToSurface.canvasId !== activeCanvasId) {
      setActiveCanvasId(scrollToSurface.canvasId);
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    const tryHighlight = () => {
      if (cancelled) return;
      const el = scrollContainerRef.current?.querySelector<HTMLElement>(
        `[data-surface-id="${scrollToSurface.surfaceId}"]`,
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Restart the animation if the class is somehow still present from
        // a prior click — toggling triggers the keyframes again.
        el.classList.remove('animate-highlight-fade');
        // Force reflow so removing + re-adding restarts the animation.
        void el.offsetWidth;
        el.classList.add('animate-highlight-fade');
        el.addEventListener(
          'animationend',
          () => el.classList.remove('animate-highlight-fade'),
          { once: true },
        );
        dispatch({ type: 'clear_scroll_target' });
        return;
      }
      attempts += 1;
      if (attempts < 5) {
        timer = setTimeout(tryHighlight, 50);
      } else {
        dispatch({ type: 'clear_scroll_target' });
      }
    };

    const raf = requestAnimationFrame(() => {
      innerRafRef.current = requestAnimationFrame(tryHighlight);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      cancelAnimationFrame(innerRafRef.current);
      if (timer) clearTimeout(timer);
    };
  }, [scrollToSurface, activeCanvasId, setActiveCanvasId, dispatch]);

  // No canvases — show empty state or nothing. The header strip is always
  // mounted so the skeleton/empty/populated panels share the same shell.
  if (!hasCanvases) {
    return (
      <div className="flex h-full flex-col" data-testid="canvas-panel">
        <div className="flex h-16 shrink-0 items-center justify-end border-b border-border pr-8">
          {headerActions}
        </div>
        <div className="min-h-0 flex-1">
          {emptyState ?? <CanvasEmptyState />}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" data-testid="canvas-panel">
      {/* Tab bar acts as the panel header. The active tab conveys the
          current canvas name; headerActions (e.g. the connection
          indicator) ride along on the right. */}
      <CanvasTabs
        canvases={canvasList}
        activeCanvasId={activeCanvasId}
        pendingUpdates={pendingUpdates}
        onSelect={setActiveCanvasId}
        onDismiss={removeCanvas}
        onReorder={reorderCanvases}
        onRename={renameCanvas}
        rightSlot={headerActions}
      />

      {/* Surface content */}
      <div ref={scrollContainerRef} className="flex flex-1 flex-col overflow-auto p-4">
        {isActiveCanvasLoading ? (
          <CanvasEmptyState loading />
        ) : (
          <SurfaceLayout
            layout={activeCanvas?.layout ?? 'stack'}
            surfaces={activeSurfaces}
            onReorder={
              activeCanvasId
                ? (ids) => reorderSurfaces(activeCanvasId, ids)
                : undefined
            }
            onAction={
              onAction && activeCanvasId
                ? (action, surfaceName) => onAction(action, surfaceName, activeCanvasId)
                : undefined
            }
          />
        )}
      </div>
    </div>
  );
}
